import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { GraduationCap, Gift, ShieldCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { IneUpload } from "../dashboard/IneUpload";
import { RegistrationClosed } from "./RegistrationClosed";
import { formatApiError } from "../../lib/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const [form, setForm] = useState({ first_name: "", lastname: "", second_lastname: "", birth_date: "", curp: "", email: "", password: "" });
  const [step, setStep] = useState(1);
  const { register, user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const giftCode = searchParams.get("gift");
  const [isLoading, setIsLoading] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/config`)
      .then(res => setRegistrationOpen(res.data.registration_open))
      .catch(() => setRegistrationOpen(true));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: k === "curp" ? e.target.value.toUpperCase().replace(/\s/g, "") : e.target.value });
  const goDashboard = () => navigate(giftCode ? `/dashboard?gift=${giftCode}` : "/dashboard");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await register({ ...form, email: form.email.trim().toLowerCase(), second_lastname: form.second_lastname.trim() || null });
    setIsLoading(false);
    if (result.success) {
      toast.success("Cuenta creada. Ahora sube tu INE.");
      setStep(2);
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-brand-lightBlue/30 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-lg p-8 md:p-12" data-testid="register-card">
        <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
                <GraduationCap className="w-8 h-8 text-brand-blue" />
                <span className="text-2xl font-bold text-brand-blue">Appadrino</span>
            </Link>
            <h1 className="text-2xl font-bold text-brand-dark mb-2">{registrationOpen === false ? "Lista de espera" : step === 1 ? "Crear Cuenta" : "Verifica tu identidad"}</h1>
            <p className="text-gray-500 text-sm">{registrationOpen === false ? "Muy pronto abriremos nuevas cuentas" : step === 1 ? "Solo padres y tutores verificados administran alcancías" : "Sube una foto del frente y reverso de tu INE"}</p>
            {registrationOpen !== false && (
              <div className="flex items-center justify-center gap-2 mt-4" data-testid="register-steps">
                {[1, 2].map((s) => <span key={s} className={`h-1.5 rounded-full transition-all ${step === s ? "w-10 bg-brand-blue" : "w-6 bg-gray-200"}`} />)}
              </div>
            )}
        </div>

        {giftCode && registrationOpen !== false && step === 1 && (
          <div data-testid="register-gift-banner" className="mb-6 flex items-center gap-3 rounded-2xl bg-brand-lightGreen p-4 text-sm text-brand-dark">
            <Gift className="w-5 h-5 text-brand-blue shrink-0" />
            <p>Tienes un regalo con el código <span className="font-mono font-bold tracking-wider">{giftCode}</span>. Crea tu cuenta para canjearlo.</p>
          </div>
        )}

        {registrationOpen === false ? (
          <RegistrationClosed />
        ) : step === 1 ? (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="rounded-xl bg-brand-lightBlue/60 p-3 text-xs text-brand-dark flex gap-2">
              <ShieldCheck className="w-4 h-4 text-brand-blue shrink-0 mt-0.5" />
              <p>Captura tus datos tal como aparecen en tu INE. Tu CURP se valida con el algoritmo oficial de RENAPO.</p>
            </div>
            <div className="space-y-2">
                <Label htmlFor="first_name">Nombre(s)</Label>
                <Input id="first_name" data-testid="register-first-name-input" placeholder="Juan Carlos" value={form.first_name} onChange={set("first_name")} required className="h-11 rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="lastname">Apellido paterno</Label>
                <Input id="lastname" data-testid="register-lastname-input" placeholder="Pérez" value={form.lastname} onChange={set("lastname")} required className="h-11 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="second_lastname">Apellido materno</Label>
                <Input id="second_lastname" data-testid="register-second-lastname-input" placeholder="López" value={form.second_lastname} onChange={set("second_lastname")} className="h-11 rounded-xl" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="birth_date">Fecha de nacimiento</Label>
                <Input id="birth_date" type="date" data-testid="register-birthdate-input" value={form.birth_date} onChange={set("birth_date")} required className="h-11 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="curp" className="flex items-center justify-between">CURP <a href="https://www.gob.mx/curp/" target="_blank" rel="noreferrer" className="text-[11px] text-brand-blue hover:underline inline-flex items-center gap-1">Consultar <ExternalLink className="w-3 h-3" /></a></Label>
                <Input id="curp" data-testid="register-curp-input" maxLength={18} placeholder="18 caracteres" value={form.curp} onChange={set("curp")} required className="h-11 rounded-xl font-mono uppercase tracking-wider" />
              </div>
            </div>
            <div className="space-y-2">
                <Label htmlFor="email">Correo electrónico</Label>
                <Input id="email" data-testid="register-email-input" type="email" placeholder="tucorreo@ejemplo.com" value={form.email} onChange={set("email")} required className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input id="password" data-testid="register-password-input" type="password" placeholder="••••••••" value={form.password} onChange={set("password")} required minLength={6} className="h-11 rounded-xl" />
            </div>
            <Button type="submit" data-testid="register-submit-btn" className="w-full h-12 bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold rounded-xl text-lg mt-2" disabled={isLoading || registrationOpen === null}>
                {isLoading ? "Validando CURP..." : "Continuar"}
            </Button>
          </form>
        ) : (
          <div className="space-y-4" data-testid="register-ine-step">
            <IneUpload onUploaded={(u) => { updateUser(u); toast.success("Documentos recibidos. ¡Bienvenido!"); goDashboard(); }} />
            <button type="button" data-testid="register-skip-ine-btn" onClick={goDashboard} className="w-full text-xs text-gray-400 hover:text-brand-blue">Subir después (no podrás crear alcancías hasta verificar)</button>
          </div>
        )}

        {step === 1 && registrationOpen !== false && (
          <div className="mt-8 text-center text-sm">
              <p className="text-gray-500">¿Ya tienes cuenta? <Link to="/login" data-testid="register-login-link" className="text-brand-blue font-bold hover:underline">Inicia sesión aquí</Link></p>
          </div>
        )}
        {user && step === 1 && registrationOpen !== false && <p className="sr-only">Sesión activa</p>}
      </div>
    </div>
  );
}
